import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { theme } from '../../styles/theme';
import api from '../../config/api';
import { useParams, useNavigate } from 'react-router-dom';

const Container = styled.div`
  max-width: 420px; margin: 4rem auto; padding: 1.5rem;
  background: ${theme.colors.background.paper};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
`;

const Title = styled.h1`
  margin: 0 0 1rem 0; color: ${theme.colors.text.primary};
`;

const Label = styled.label`
  display:block; color:${theme.colors.text.secondary}; font-size:0.875rem; margin-bottom:0.25rem;
`;

const Input = styled.input`
  width:100%; padding:0.75rem; border-radius:${theme.borderRadius.md}; border:1px solid ${theme.colors.border.primary}; background:${theme.colors.background.secondary}; color:${theme.colors.text.primary};
`;

const Button = styled.button`
  margin-top: 1rem; padding:0.5rem 1rem; border-radius:${theme.borderRadius.md}; border:1px solid ${theme.colors.border.primary}; background:${theme.colors.primary.main}; color:white;
`;

export const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault(); setError('');
    try {
      await api.post('/api/auth/forgot-password', { email });
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send reset email');
    }
  };

  return (
    <Container>
      <Title>Forgot Password</Title>
      {sent ? (
        <p style={{ color: theme.colors.text.secondary }}>If that email exists, a reset link has been sent.</p>
      ) : (
        <form onSubmit={submit}>
          <Label>Email</Label>
          <Input type="email" value={email} onChange={e=>setEmail(e.target.value)} required />
          {error && <div style={{ color: theme.colors.error.main, marginTop: '0.5rem' }}>{error}</div>}
          <Button type="submit">Send Reset Link</Button>
        </form>
      )}
    </Container>
  );
};

// Deprecated simple ResetPassword component removed in favor of robust PasswordReset below

// Note: Keep ForgotPassword as a named export only; default export defined below for PasswordReset

const PasswordReset = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [tokenValid, setTokenValid] = useState(null);

  const verifyToken = useCallback(async () => {
    try {
      await api.get(`/api/auth/verify-reset-token/${token}`);
      setTokenValid(true);
    } catch (error) {
      console.error('Token verification failed:', error);
      setTokenValid(false);
      setError('Invalid or expired reset token');
    }
  }, [token]);

  useEffect(() => {
    // Verify token is valid when component loads
    if (token) {
      verifyToken();
    }
  }, [token, verifyToken]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters long');
      setLoading(false);
      return;
    }

    try {
      await api.post(`/api/auth/reset-password`, {
        token,
        password: formData.password
      });
      
      setSuccess(true);
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (error) {
      console.error('Password reset failed:', error);
      setError(error.response?.data?.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  if (tokenValid === null) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{backgroundColor: '#0f172a'}}>
        <div className="text-white">Verifying token...</div>
      </div>
    );
  }

  if (tokenValid === false) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{backgroundColor: '#0f172a'}}>
        <div className="max-w-md w-full p-8 rounded-lg shadow-lg" style={{backgroundColor: '#1e293b'}}>
          <div className="text-center">
            <h2 className="text-2xl font-bold mb-4" style={{color: '#ef4444'}}>Invalid Token</h2>
            <p className="text-gray-300 mb-6">This password reset link is invalid or has expired.</p>
            <button
              onClick={() => navigate('/login')}
              className="w-full py-2 px-4 rounded-md font-medium transition-colors"
              style={{backgroundColor: '#1e40af', color: 'white'}}
            >
              Return to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{backgroundColor: '#0f172a'}}>
        <div className="max-w-md w-full p-8 rounded-lg shadow-lg" style={{backgroundColor: '#1e293b'}}>
          <div className="text-center">
            <h2 className="text-2xl font-bold mb-4" style={{color: '#10b981'}}>Password Reset Successful</h2>
            <p className="text-gray-300 mb-6">Your password has been updated successfully. You will be redirected to the login page.</p>
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 mx-auto" style={{borderColor: '#10b981'}}></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center" style={{backgroundColor: '#0f172a'}}>
      <div className="max-w-md w-full p-8 rounded-lg shadow-lg" style={{backgroundColor: '#1e293b'}}>
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold mb-2" style={{color: '#60a5fa'}}>Reset Password</h2>
          <p className="text-gray-300">Enter your new password below</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="password" className="block text-sm font-medium mb-2" style={{color: '#e2e8f0'}}>
              New Password
            </label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 rounded-md border focus:outline-none focus:ring-2 focus:ring-blue-500"
              style={{
                backgroundColor: '#334155',
                borderColor: '#475569',
                color: '#e2e8f0'
              }}
              placeholder="Enter new password"
            />
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium mb-2" style={{color: '#e2e8f0'}}>
              Confirm Password
            </label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 rounded-md border focus:outline-none focus:ring-2 focus:ring-blue-500"
              style={{
                backgroundColor: '#334155',
                borderColor: '#475569',
                color: '#e2e8f0'
              }}
              placeholder="Confirm new password"
            />
          </div>

          {error && (
            <div className="p-3 rounded-md" style={{backgroundColor: '#7f1d1d', color: '#fecaca'}}>
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full py-2 px-4 rounded-md font-medium transition-colors disabled:opacity-50"
            style={{backgroundColor: '#1e40af', color: 'white'}}
          >
            {loading ? 'Resetting Password...' : 'Reset Password'}
          </button>
        </form>

        <div className="mt-6 text-center">
          <button
            onClick={() => navigate('/login')}
            className="text-sm text-blue-400 hover:text-blue-300"
          >
            Back to Login
          </button>
        </div>
      </div>
    </div>
  );
};

export const ResetPassword = PasswordReset;
export default PasswordReset;
